import { gozokiApiWithAuth } from '../gozokiApi'
import type { DocumentItem, StoreProjectResponse } from '../queries/storeProjects.queries'

// Create a store project
export const createStoreProject = async (data: Partial<StoreProjectResponse>) => {
  const response = await gozokiApiWithAuth.post<StoreProjectResponse>('/store-projects', data)
  return response.data
}

// Update a store project
export const updateStoreProject = async ({
  id,
  data,
}: {
  id: string
  data: Partial<StoreProjectResponse>
}) => {
  const response = await gozokiApiWithAuth.patch<StoreProjectResponse>(
    `/store-projects/${id}`,
    data,
  )
  return response.data
}

// Delete a store project
export const deleteStoreProject = async (id: string) => {
  const response = await gozokiApiWithAuth.delete(`/store-projects/${id}`)
  return response
}

// Update store project status (for drag-and-drop) - DEPRECATED, use updateStoreProjectPosition instead
export const updateStoreProjectStatus = async ({
  id,
  status,
}: {
  id: string
  status: 'before_project' | 'under_study' | 'secured' | 'works' | 'open'
}) => {
  const response = await gozokiApiWithAuth.patch<{ id: string; status: string }>(
    `/store-projects/${id}/status`,
    { status },
  )
  return response.data
}

// Update store project position (handles both status and position changes)
export const updateStoreProjectPosition = async ({
  id,
  status,
  position,
}: {
  id: string
  status: 'before_project' | 'under_study' | 'secured' | 'works' | 'open'
  position: number
}) => {
  const response = await gozokiApiWithAuth.patch<{
    id: string
    status: string
    displayOrder: number
  }>(`/store-projects/${id}/position`, { status, position })
  return response.data
}

// Upload photo for a store project
export const uploadStoreProjectPhoto = async ({ id, photo }: { id: string; photo: File }) => {
  const formData = new FormData()
  formData.append('photo', photo)

  const response = await gozokiApiWithAuth.post<{ url: string; photoUrls: string[] }>(
    `/store-projects/${id}/upload-photo`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response.data
}
// Delete photo from a store project
export const deleteStoreProjectPhoto = async ({
  id,
  photoUrl,
}: {
  id: string
  photoUrl: string
}) => {
  const response = await gozokiApiWithAuth.delete<{ success: boolean; photoUrls: string[] }>(
    `/store-projects/${id}/delete-photo`,
    {
      data: { photoUrl },
    },
  )
  return response.data
}

// Upload document for a store project
export const uploadStoreProjectDocument = async ({
  id,
  document,
}: {
  id: string
  document: File
}) => {
  const formData = new FormData()
  formData.append('document', document)

  const response = await gozokiApiWithAuth.post<{
    document: DocumentItem
    documents: DocumentItem[]
  }>(`/store-projects/${id}/upload-document`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

// Delete document from a store project
export const deleteStoreProjectDocument = async ({
  id,
  documentUrl,
}: {
  id: string
  documentUrl: string
}) => {
  const response = await gozokiApiWithAuth.delete<{
    success: boolean
    documents: DocumentItem[]
  }>(`/store-projects/${id}/delete-document`, {
    data: { documentUrl },
  })
  return response.data
}

// Rename document in a store project
export const renameStoreProjectDocument = async ({
  id,
  documentUrl,
  newName,
}: {
  id: string
  documentUrl: string
  newName: string
}) => {
  const response = await gozokiApiWithAuth.patch<{
    success: boolean
    documents: DocumentItem[]
  }>(`/store-projects/${id}/rename-document`, {
    documentUrl,
    newName,
  })
  return response.data
}
